"use client";
import { Fragment, jsx, jsxs } from "react/jsx-runtime";
import { useCallback, useRef, useState } from "react";
import { cn } from "../../utils/index.js";
import { kenBurnsEffectClassMap } from "../animations/KenBurnsEffect/kenBurnsEffectClasses.js";
import { ContentfulImgOptimizer } from "../ContentfulImgOptimizer/index.js";
import { Heading } from "../Heading/index.js";
import { Highlight } from "../Highlight/index.js";
import { Image } from "../Image/index.js";
import { MerchDisplayPill } from "../MerchDisplay/index.js";
import { Scrim } from "../Scrim/index.js";
import { P } from "../Text/index.js";
import { mediaSnippetVariants } from "./MediaSnippet.variants.js";
const KEN_BURNS_CENTER_LONG = kenBurnsEffectClassMap.find((effect)=>'Center Long' === effect.name);
const { rootSlot: rootSlot, mediaWrapperSlot: mediaWrapperSlot, mediaImageSlot: mediaImageSlot, vignetteSlot: vignetteSlot, mediaHeaderSlot: mediaHeaderSlot, mediaHeaderLeftSlot: mediaHeaderLeftSlotClass, mediaHeaderRightSlot: mediaHeaderRightSlotClass, scrimSlot: scrimSlot, captionSlot: captionSlot, headingSlot: headingSlot, bodySlot: bodySlot } = mediaSnippetVariants();
function isContentfulUrl(url) {
    return 'string' == typeof url && url.includes('ctfassets.net');
}
function MediaSnippet({ heading, body, alt, imageUrl, ratio = '1/1', showAd = false, discountText, mediaHeaderLeftSlot, mediaHeaderRightSlot, vignette = false, caption, role, href, onClick, anchorProps, buttonProps, ariaLabel, children, slots = {} }) {
    const [isHovered, setIsHovered] = useState(false);
    const hasHoveredRef = useRef(false);
    const handleMouseEnter = useCallback(()=>{
        hasHoveredRef.current = true;
        setIsHovered(true);
    }, []);
    const handleMouseLeave = useCallback(()=>{
        setIsHovered(false);
    }, []);
    const isInteractive = null != role;
    const kenBurnsDirection = isHovered ? KEN_BURNS_CENTER_LONG?.in : KEN_BURNS_CENTER_LONG?.out;
    const kenBurnsClass = isInteractive && hasHoveredRef.current ? kenBurnsDirection : void 0;
    const isAutoRatio = 'auto' === ratio;
    const resolvedRatio = isAutoRatio ? void 0 : ratio;
    const leftHeaderContent = mediaHeaderLeftSlot || discountText && /*#__PURE__*/ jsx(MerchDisplayPill, {
        discountText: discountText,
        alignment: "centered",
        theme: "vipBlue"
    });
    const rightHeaderContent = mediaHeaderRightSlot || showAd && /*#__PURE__*/ jsx(Highlight, {
        palette: "neutral",
        textStyle: "disclaimer",
        children: "Ad"
    });
    const imageElement = isContentfulUrl(imageUrl) ? /*#__PURE__*/ jsx(ContentfulImgOptimizer, {
        contentfulUrl: imageUrl,
        alt: alt,
        ratio: resolvedRatio,
        width: "full",
        height: "full"
    }) : /*#__PURE__*/ jsx(Image, {
        src: imageUrl,
        alt: alt,
        ratio: resolvedRatio,
        width: "full",
        height: "full"
    });
    const sharedProps = {
        className: cn(rootSlot(), isInteractive && 'group cursor-pointer'),
        ...isInteractive && {
            onMouseEnter: handleMouseEnter,
            onMouseLeave: handleMouseLeave
        }
    };
    const innerContent = /*#__PURE__*/ jsxs(Fragment, {
        children: [
            /*#__PURE__*/ jsxs("div", {
                className: mediaWrapperSlot(),
                children: [
                    isAutoRatio ? /*#__PURE__*/ jsx("div", {
                        className: cn(kenBurnsClass),
                        children: imageElement
                    }) : /*#__PURE__*/ jsxs(Fragment, {
                        children: [
                            /*#__PURE__*/ jsx("div", {
                                className: cn(mediaImageSlot(), kenBurnsClass),
                                children: imageElement
                            }),
                            /*#__PURE__*/ jsx("div", {
                                className: `aspect-${ratio}`,
                                style: {
                                    aspectRatio: ratio
                                },
                                "aria-hidden": true
                            })
                        ]
                    }),
                    vignette && /*#__PURE__*/ jsx("div", {
                        className: vignetteSlot(),
                        "aria-hidden": true
                    }),
                    (leftHeaderContent || rightHeaderContent) && /*#__PURE__*/ jsxs("div", {
                        className: mediaHeaderSlot(),
                        children: [
                            /*#__PURE__*/ jsx("div", {
                                className: mediaHeaderLeftSlotClass(),
                                children: leftHeaderContent
                            }),
                            /*#__PURE__*/ jsx("div", {
                                className: mediaHeaderRightSlotClass(),
                                children: rightHeaderContent
                            })
                        ]
                    }),
                    caption && /*#__PURE__*/ jsx("div", {
                        className: scrimSlot(),
                        children: /*#__PURE__*/ jsx(Scrim, {
                            isBottom: true,
                            padding: 3,
                            children: /*#__PURE__*/ jsx(P, {
                                textStyle: "body2",
                                className: cn(captionSlot(), slots.captionSlot),
                                children: caption
                            })
                        })
                    })
                ]
            }),
            heading && /*#__PURE__*/ jsx(Heading, {
                as: "h6",
                textWrap: "pretty",
                className: headingSlot(),
                children: heading
            }),
            body && /*#__PURE__*/ jsx(P, {
                textStyle: "body2",
                className: bodySlot(),
                children: body
            }),
            children
        ]
    });
    if ('link' === role) return /*#__PURE__*/ jsx("a", {
        href: href,
        "aria-label": ariaLabel,
        onClick: onClick,
        ...anchorProps,
        ...sharedProps,
        children: innerContent
    });
    if ('button' === role) return /*#__PURE__*/ jsx("button", {
        type: "button",
        "aria-label": ariaLabel,
        onClick: onClick,
        ...buttonProps,
        ...sharedProps,
        children: innerContent
    });
    return /*#__PURE__*/ jsx("div", {
        ...sharedProps,
        children: innerContent
    });
}
export { MediaSnippet };
